import { UserIsAuthenticated, UserHasValidPassword, UserHasScope } from 'containers/authContainers'
import LockRedirect from 'containers/lockContainers'
import { browserHistory } from 'utils/navigation'
import { INSTRUMENT_TYPE_MAP } from 'utils/constants'
import { setPageTitle } from 'utils/misc'
import { injectReducers } from 'store/reducers'
import SchedulerView from './containers/SchedulerContainer'
import { clearInstrumentTriggers, instrumentActions, schedulerActions } from '../../modules/Instrument'
import instruments, { fetchInstruments } from '../../../InstrumentsPage/modules/Instruments'
import { fetchClinroRoles } from '../../../../../Users/routes/CreateUser/modules/CreateUser'
import { fetchCohorts } from '../../../../../Participants/routes/ParticipantsPage/modules/Participants'
import { fetchSurvey } from '../EditSurvey/modules/Survey'
import { fetchClinro } from '../EditClinro/modules/Clinro'
import { fetchDiary } from '../EditDiary/modules/Diary'
import { fetchInstrumentConditions } from '../../../../../Notifications/routes/EditConditionalNotification/modules/ConditionalNotification'
import sitesReducer, { fetchSites } from '../../../../../Sites/routes/SitesPage/modules/Sites'
import hasInstrumentFeatures from '../../../../utils/hasInstrumentFeatures'

/**
 * All Schedules → Edit/New Schedule, and Properties → Schedule, pass
 * keepTriggerDraft so the in-memory schedule (including a new multi-schedule
 * draft such as Triggered by Visit) survives that round-trip. Do not infer this
 * from Redux prevPathname: leaving for the instruments list and reopening the
 * same instrument still has prevPathname `/schedules`, which would keep a
 * deleted draft instead of the saved server triggers.
 */
export function shouldKeepInstrumentTriggerDraft(nextState) {
  const routeState = nextState?.location?.state || nextState?.state
  return !!routeState?.keepTriggerDraft
}

// Sync route definition
export default store => ({
  path: ':instrumentID/schedule',
  /*  Async getComponent is only invoked when route matches   */
  getComponent(nextState, cb) {
    /*  Webpack - use 'require.ensure' to create a split point
     and embed an async module loader (jsonp) when bundling   */
    const ensure = require.ensure || ((dependencies, callback) => callback())
    ensure(
      [],
      async () => {
        /*  Webpack - use require callback to define
       dependencies for bundling   */

        injectReducers(store, {
          sites: sitesReducer,
          instruments,
        })
        const { studyID, instrumentID, studyVersionID } = nextState.params
        const { currentStudy } = store.getState().study
        setPageTitle(`Schedule | Study ${studyID} | ${currentStudy.platform_name}`)

        await store.dispatch(fetchSites(studyID, true, false, false)) // params: studyID, hasLoader, includeLang, clearCheckedSites
        await store.dispatch(fetchCohorts(studyID, false))

        const existingInstrument = store.getState()?.instrumentReducer?.instrument
        const hasLoadedInstrument =
          existingInstrument?.id && String(existingInstrument.id) === String(instrumentID)
        const triggerState = store.getState()?.instrumentReducer?.instrumentTriggers || {}
        // All Schedules → Edit/New Schedule is the only path that should keep an unsaved
        // trigger draft (including an empty list after delete). Opening the instrument
        // from the list is a fresh load and must come from the server.
        const fromAllSchedules = shouldKeepInstrumentTriggerDraft(nextState)
        const keepScheduleDraft = hasLoadedInstrument && fromAllSchedules
        const shouldRestoreTriggers = !keepScheduleDraft || !!triggerState.needsHydration

        if (shouldRestoreTriggers) {
          store.dispatch(clearInstrumentTriggers())
        }

        const applyScheduleIndexFromQuery = () => {
          // Study Timeline Edit Schedule navigates with ?scheduleIndex=N so we open that schedule
          // after the instrument (and confirmedSchedules) are ready.
          const search =
            nextState.location?.search ||
            store.getState().location?.search ||
            (typeof window !== 'undefined' ? window.location.search : '') ||
            ''
          const raw = new URLSearchParams(search).get('scheduleIndex')
          if (raw == null || raw === '') return
          const idx = Number(raw)
          if (!Number.isInteger(idx) || idx < 0) return
          store.dispatch(schedulerActions.loadScheduleForEdit(idx))
        }

        const afterInstrumentReady = (instrument, { restoreTriggers = true } = {}) => {
          const type = instrument?.type
          if (!type) return

          const {
            currentStudy: { config },
          } = store.getState().study

          if (!hasInstrumentFeatures(config, type))
            browserHistory.push(`/studies/${studyID}/${studyVersionID}/instruments`)
          const isClinroOrSurvey = [INSTRUMENT_TYPE_MAP.clinro, INSTRUMENT_TYPE_MAP.survey].includes(type)
          if (isClinroOrSurvey) {
            if (type === INSTRUMENT_TYPE_MAP.clinro) {
              store.dispatch(fetchClinroRoles())
              store.dispatch(fetchClinro(studyID, instrumentID))
            } else if (type === INSTRUMENT_TYPE_MAP.survey) {
              store.dispatch(fetchSurvey(studyID, instrumentID))
            }
          }
          if (type === INSTRUMENT_TYPE_MAP.diary) store.dispatch(fetchDiary(studyID, instrumentID))
          const { instrumentReducer } = store.getState()
          const metadata = instrumentReducer?.instrument?.metadata || {}
          const { conditions, conditions_data } = metadata

          if (restoreTriggers) {
            // If an instrument has conditions aka trigger-actions, we will fetch and set the instrument triggers
            if (conditions?.length) {
              store.dispatch(fetchInstrumentConditions(studyID, conditions))
            } else if (conditions_data?.conditions?.length) {
              // Draft or imported trigger config: restore when condition IDs are missing
              conditions_data.conditions.forEach(condition => {
                const conditionWithId = {
                  ...condition,
                  triggers: condition.triggers.map(trigger => {
                    return {
                      ...trigger,
                      resource_function_params: {
                        ...trigger.resource_function_params,
                        instrument_id: instrumentID,
                      },
                    }
                  }),
                }
                delete conditionWithId.condition_id
                store.dispatch(
                  instrumentActions.addInstrumentTrigger({
                    instrumentId: '',
                    isScoringTrigger: false,
                    trigger: conditionWithId,
                  }),
                )
              })
            }
          }

          applyScheduleIndexFromQuery()
        }

        // Skip refetch when navigating from All Schedules (New/Edit Schedule) so the
        // in-memory draft / confirmedSchedules list is not overwritten.
        if (keepScheduleDraft) {
          afterInstrumentReady(existingInstrument, { restoreTriggers: shouldRestoreTriggers })
        } else {
          store.dispatch(instrumentActions.onFetchInstrument(studyID, instrumentID)).then(afterInstrumentReady)
        }
        /**
         * Second arguments in the following fetch functions are booleans that represent whether
         * these requests should affect loading state that can potentially stop the UI loader before the
         * instrument is loaded.
         */
        // Avoid redundant refetches on route transitions.
        // We already fetched sites/cohorts above; and instruments list is often already present from InstrumentsPage.
        const existingInstrumentList = store.getState()?.instruments?.instrumentList
        if (!Array.isArray(existingInstrumentList) || existingInstrumentList.length === 0) {
          store.dispatch(fetchInstruments({ studyID, hasLoader: false }))
        }

        /*  Return getComponent   */
        cb(
          null,
          UserIsAuthenticated(
            UserHasValidPassword(
              LockRedirect(studyID, studyVersionID, 'instruments')(UserHasScope(['wa'], SchedulerView, false, studyID)),
            ),
          ),
        )

        /* Webpack named bundle   */
      },
      'instruments/schedule',
    )
  },
})
